export const SELF_SERVICE_INTENTS = [
  { code: 'INQUIRY', label: '找货/商品识别' },
  { code: 'ORDER', label: '查订单' },
  { code: 'LOGISTICS', label: '查物流' },
  { code: 'INVOICE', label: '查发票' },
  { code: 'PAYMENT', label: '查支付' },
  { code: 'CONTRACT', label: '查合同' },
]

export const CHAT_QUICK_PROMPTS = [
  { label: '最近一单', prompt: '最近一单', intentCode: 'ORDER' },
  { label: '查订单', prompt: '查订单 商城-20260411-000039', intentCode: 'ORDER' },
  { label: '近7天物流', prompt: '近7天物流', intentCode: 'LOGISTICS' },
  { label: '查发票状态', prompt: '查发票状态', intentCode: 'INVOICE' },
  { label: '查支付状态', prompt: '付款了吗', intentCode: 'PAYMENT' },
  { label: '查合同', prompt: '这个订单能下载合同吗', intentCode: 'CONTRACT' },
]

const BUSINESS_FIELD_LABELS = {
  orderNo: '订单号',
  orderNumber: '订单号',
  saleOrderNo: '订单号',
  mallOrderNo: '商城订单号',
  status: '状态',
  orderStatus: '订单状态',
  payStatus: '支付状态',
  paymentStatus: '支付状态',
  deliveryStatus: '发货状态',
  shipStatus: '发货状态',
  shippingStatus: '发货状态',
  receiveStatus: '收货状态',
  logisticsStatus: '物流状态',
  logisticsSummary: '物流摘要',
  expressNo: '运单号',
  trackingNo: '运单号',
  carrier: '承运商',
  invoiceStatus: '发票状态',
  invoiceApplyStatus: '开票申请状态',
  invoiceNo: '发票号',
  invoiceUrl: '发票下载',
  contractUrl: '合同下载',
  createTime: '下单时间',
  orderTime: '下单时间',
  totalAmount: '订单金额',
  receivedAmount: '已收金额',
  unpaidAmount: '未结金额',
  amount: '金额',
}

const BUSINESS_FIELD_ORDER = Object.keys(BUSINESS_FIELD_LABELS)
const NEXT_STEPS = [
  { label: '换完整订单号', prompt: '我换完整订单号再查：' },
  { label: '按时间范围查', prompt: '按时间查 2026-07-01到2026-07-05' },
]

let messageSeed = 0

export function createChatMessage(role, payload = {}) {
  messageSeed += 1
  return {
    id: `${role}-${Date.now()}-${messageSeed}`,
    role,
    ...payload,
  }
}

export function createWelcomeMessages() {
  return [{
    id: 'assistant-welcome',
    role: 'assistant',
    intentCode: 'GENERAL',
    text: '您好，我可以直接帮您查订单、物流、发票、支付、合同，也可以根据描述识别并匹配商品。您可以像和客服说话一样输入问题。',
    suggestions: CHAT_QUICK_PROMPTS.map((item) => ({ ...item })),
  }]
}

export function serviceIntentLabel(intentCode) {
  if (intentCode === 'AUTO') return 'AI识别'
  if (intentCode === 'GENERAL') return 'AI对话'
  return SELF_SERVICE_INTENTS.find((item) => item.code === intentCode)?.label || '自助查询'
}

export function tryParseJson(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export function resultJsonPayload(data = {}) {
  return tryParseJson(data.result_json)
    || tryParseJson(data.resultJson)
    || tryParseJson(data.task?.result_json)
    || tryParseJson(data.task?.resultJson)
    || tryParseJson(data.result)
    || null
}

function normalizeBusinessCandidates(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object')
  if (typeof value !== 'object') return []
  return [value]
}

function hasBusinessFields(item = {}) {
  return BUSINESS_FIELD_ORDER.some((key) => item[key] !== undefined && item[key] !== null && item[key] !== '')
}

export function collectBusinessRows(data = {}) {
  const payload = resultJsonPayload(data) || {}
  const candidates = [
    data,
    data.order,
    data.orders,
    data.logistics,
    data.invoice,
    data.invoices,
    payload,
    payload.order,
    payload.orders,
    payload.logistics,
    payload.invoice,
    payload.invoices,
  ]
  const seen = new Set()
  return candidates
    .flatMap(normalizeBusinessCandidates)
    .filter(hasBusinessFields)
    .filter((item) => {
      const key = String(item.orderNo || item.saleOrderNo || item.invoiceNo || JSON.stringify(item))
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 4)
}

export function businessFieldsForRow(row = {}) {
  return BUSINESS_FIELD_ORDER
    .filter((key) => row[key] !== undefined && row[key] !== null && row[key] !== '')
    .map((key) => ({
      key,
      label: BUSINESS_FIELD_LABELS[key],
      value: row[key],
    }))
}

export function isExternalUrl(value) {
  return /^(https?:\/\/|\/(?!\/))/i.test(String(value || ''))
}

export function followUpSuggestions(intentCode, data = {}) {
  if (intentCode === 'GENERAL' || intentCode === 'INQUIRY') return []
  return NEXT_STEPS.map((item) => ({ ...item }))
}

export function summarizeInquiryResult(data = {}) {
  const items = Array.isArray(data.items)
    ? data.items
    : [...(Array.isArray(data.matched) ? data.matched : []), ...(Array.isArray(data.unmatched) ? data.unmatched : [])]
  const unmatchedCount = Array.isArray(data.items)
    ? data.items.filter((item) => item.matchStatus === 'UNMATCHED').length
    : (Array.isArray(data.unmatched) ? data.unmatched.length : 0)
  let summary
  if (items.length) {
    summary = unmatchedCount
      ? `我已识别到 ${items.length} 条需求，其中 ${unmatchedCount} 条未匹配，请补充 CAS、货号、规格或品牌后再试。`
      : `我已识别到 ${items.length} 条需求，匹配到的商品已放在下方，确认后可以加入购物车。`
  } else {
    summary = '我已收到商品识别需求。请尽量补充 CAS、货号、规格、数量或截图，我会继续匹配商城商品。'
  }
  return summary
}

export function assistantAnswerText(data = {}, intentCode = 'GENERAL') {
  const payload = resultJsonPayload(data) || {}
  if (intentCode === 'INQUIRY'
    && (Array.isArray(data.items) || Array.isArray(data.matched) || Array.isArray(data.unmatched))) {
    return summarizeInquiryResult(data)
  }
  return data.answer
    || data.message
    || payload.answer
    || payload.summary
    || payload.customerVisibleText
    || '我先查到这里。您可以继续补充订单号、时间范围或商品信息，我再帮您往下查。'
}

export function buildAssistantMessage(data = {}, intentCode = 'GENERAL') {
  const resolvedIntent = data.intentCode || intentCode
  return createChatMessage('assistant', {
    intentCode: resolvedIntent,
    text: assistantAnswerText(data, resolvedIntent),
    businessRows: collectBusinessRows(data),
    suggestions: followUpSuggestions(resolvedIntent, data),
  })
}

function positiveQuantity(value) {
  return Math.max(1, Math.ceil(Number(value) || 1))
}

function normalizeCurrentInquiryItems(data = {}) {
  const items = Array.isArray(data.items) ? data.items : []
  return items.map((item, index) => {
    const matched = item.matchStatus !== 'UNMATCHED' && Boolean(item.skuId || item.productId)
    return {
      key: String(item.id || item.skuId || `${item.keyword || 'item'}-${index}`),
      inquiryItemId: item.id,
      keyword: item.keyword || item.rawDesc || '',
      rawDesc: item.rawDesc || '',
      quantity: positiveQuantity(item.quantity),
      productId: item.productId || item.skuId || null,
      skuId: item.skuId || null,
      productName: item.productName || item.nameZh || item.keyword || '未命名商品',
      cas: item.cas || '',
      skuCode: String(item.xiProCode || '').trim(),
      brand: item.brand || '',
      spec: item.spec || item.customPack || '',
      salePrice: item.salePrice ?? item.price ?? null,
      stockText: item.stockText || item.stock || '',
      matchStatus: item.matchStatus || (matched ? 'MATCHED' : 'UNMATCHED'),
      dangerous: item.dangerous === true || item.riskCodes?.includes('DANGEROUS_GOODS'),
      riskLevel: item.riskLevel || 'LOW',
      riskCodes: Array.isArray(item.riskCodes) ? item.riskCodes : [],
      reason: item.reason || '',
      matched,
    }
  })
}

function normalizeLegacyInquiryItems(data = {}) {
  const matched = Array.isArray(data.matched) ? data.matched : []
  const unmatched = Array.isArray(data.unmatched) ? data.unmatched : []
  return [
    ...matched.map((item, index) => ({
      key: String(item.skuId || item.productId || `${item.key || 'matched'}-${index}`),
      keyword: item.key || item.rawDesc || '',
      rawDesc: item.rawDesc || '',
      quantity: positiveQuantity(item.quantity),
      productId: item.productId || item.skuId || null,
      skuId: item.skuId || null,
      productName: item.nameZh || item.productName || item.key || '未命名商品',
      cas: item.cas || '',
      skuCode: String(item.xiProCode || '').trim(),
      brand: item.brand || '',
      spec: item.spec || item.customPack || '',
      salePrice: item.salePrice ?? item.price ?? null,
      stockText: item.stockText || item.stock || '',
      matchStatus: 'MATCHED',
      dangerous: item.dangerous === true,
      riskLevel: item.dangerous ? 'HIGH' : 'LOW',
      riskCodes: item.dangerous ? ['DANGEROUS_GOODS'] : [],
      reason: '',
      matched: Boolean(item.skuId || item.productId),
    })),
    ...unmatched.map((item, index) => ({
      key: `unmatched-${item.key || item.rawDesc || index}`,
      keyword: item.key || item.rawDesc || '',
      rawDesc: item.rawDesc || '',
      quantity: positiveQuantity(item.quantity),
      productId: null,
      skuId: null,
      productName: item.nameZh || item.key || item.rawDesc || '未匹配商品',
      cas: item.cas || '',
      skuCode: '',
      brand: item.brand || '',
      spec: item.spec || '',
      salePrice: null,
      stockText: '',
      matchStatus: 'UNMATCHED',
      dangerous: false,
      riskLevel: 'MEDIUM',
      riskCodes: ['NO_MATCH'],
      reason: item.reason || '',
      matched: false,
    })),
  ]
}

export function normalizeInquiryResult(data = {}) {
  const hasCurrentItems = Array.isArray(data.items)
  const items = hasCurrentItems ? normalizeCurrentInquiryItems(data) : normalizeLegacyInquiryItems(data)
  return {
    orderInfo: {
      customerName: data.customerName || data.customer?.customer || '',
      contactPerson: data.contactPerson || data.customer?.contactPerson || data.customer?.contact || '',
      contactPhone: data.contactPhone || data.customer?.phone || '',
      contactEmail: data.contactEmail || data.customer?.email || '',
      address: data.address || data.customer?.address || '',
    },
    items,
  }
}

export function cartPayloadFromResultItem(item = {}) {
  if (!item.matched || !item.skuId) return null
  return {
    id: item.skuId,
    spuId: item.productId || item.skuId,
    count: positiveQuantity(item.quantity),
    name: item.productName,
    productName: item.productName,
    cas: item.cas,
    proCode: item.skuCode,
    brand: item.brand,
    spec: item.spec,
    price: item.salePrice,
  }
}
